import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Library, Users, Check, Share2, FileText, Star, ShoppingBag } from "lucide-react";

const TITLE = "The Detective Diaries of Marchfield Meadows";
const AUTHOR = "Terata B.";
const ISBN = "9781067688004";
const ASIN = "1067688005";
const AMAZON_URL = `https://www.amazon.com/Detective-Diaries-Marchfield-Meadows/dp/${ASIN}`;
const AMAZON_REVIEW_URL = `https://www.amazon.com/review/create-review?asin=${ASIN}`;
const SITE_URL = typeof window !== "undefined" ? window.location.origin : "https://farmsmartkids.com";

const SHORT_PITCH = `Just discovered "${TITLE}" by ${AUTHOR} — a charming children's mystery series starring Callie the Case-Solving Cow! 🐄🔍 Now on Amazon: ${AMAZON_URL}`;
const SHORT_PITCH_NO_URL = `Just discovered "${TITLE}" by ${AUTHOR} — a charming children's mystery series starring Callie the Case-Solving Cow! 🐄🔍 Now on Amazon!`;

const buildLibrarianMail = () => {
  const subject = `Library/School request: ${TITLE} (ISBN ${ISBN})`;
  const body = `Hi there,

I'd love to see "${TITLE}" by ${AUTHOR} added to our library/classroom collection. It's a brand new children's farm-mystery picture book that kids would adore.

Here's what you'd need to order it through your usual wholesale channel (IngramSpark / Ingram Content Group):

  Title:  ${TITLE}
  Author: ${AUTHOR}
  ISBN-13: ${ISBN}

It's also live on Amazon (and all marketplaces worldwide):
${AMAZON_URL}

A free Reading Group Kit (15 discussion questions) is available at:
${SITE_URL}/reading-group-kit

More info & buying options: ${SITE_URL}/where-to-buy

Thanks so much for considering it!
`;
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const buildFriendMail = () => {
  const subject = `You'll love this children's book — ${TITLE}`;
  const body = `${SHORT_PITCH_NO_URL}

Title: ${TITLE}
Author: ${AUTHOR}

Buy on Amazon (any marketplace): ${AMAZON_URL}

More info & retailers: ${SITE_URL}/where-to-buy
`;
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const enc = encodeURIComponent;
// For social shares, we link directly to Amazon so taps convert to purchases.
const SHARE_URLS = {
  twitter: `https://twitter.com/intent/tweet?text=${enc(SHORT_PITCH_NO_URL)}&url=${enc(AMAZON_URL)}`,
  facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(AMAZON_URL)}&quote=${enc(SHORT_PITCH_NO_URL)}`,
  whatsapp: `https://wa.me/?text=${enc(SHORT_PITCH)}`
};

export const ShareSection = ({ variant = "full", className = "" }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = AMAZON_URL;

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`tactile-card p-6 ${className}`}
      data-testid="share-section"
    >
      <h2 className="text-2xl font-bold text-[#1D3557] mb-2 flex items-center gap-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
        <Share2 className="w-7 h-7 text-[#E63946]" />
        Help Callie find more readers
      </h2>
      <p className="text-[#1D3557]/80 font-medium mb-6">
        One share goes a long way. Pick what works for you:
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Librarian CTA */}
        <a
          href={buildLibrarianMail()}
          className="group flex items-center gap-4 p-4 rounded-2xl border-3 border-[#2A9D8F] bg-[#2A9D8F]/10 hover:bg-[#2A9D8F]/20 transition-all"
          data-testid="share-librarian-btn"
        >
          <div className="w-12 h-12 rounded-xl bg-[#2A9D8F] flex items-center justify-center flex-shrink-0">
            <Library className="w-6 h-6 text-white" />
          </div>
          <div className="text-left flex-1">
            <p className="font-bold text-[#1D3557]" style={{ fontFamily: "'Fredoka', sans-serif" }}>Share with a Librarian</p>
            <p className="text-sm text-[#1D3557]/70 font-medium">Pre-filled email with ISBN — ask them to order it</p>
          </div>
        </a>

        {/* Friend CTA */}
        <a
          href={buildFriendMail()}
          className="group flex items-center gap-4 p-4 rounded-2xl border-3 border-[#E9C46A] bg-[#E9C46A]/20 hover:bg-[#E9C46A]/30 transition-all"
          data-testid="share-friend-btn"
        >
          <div className="w-12 h-12 rounded-xl bg-[#E9C46A] flex items-center justify-center flex-shrink-0">
            <Users className="w-6 h-6 text-[#1D3557]" />
          </div>
          <div className="text-left flex-1">
            <p className="font-bold text-[#1D3557]" style={{ fontFamily: "'Fredoka', sans-serif" }}>Tell a Friend</p>
            <p className="text-sm text-[#1D3557]/70 font-medium">Email a parent, teacher, or grandparent</p>
          </div>
        </a>

        {/* Reading Group Kit CTA */}
        <Link
          to="/reading-group-kit"
          className="group flex items-center gap-4 p-4 rounded-2xl border-3 border-[#E63946] bg-[#E63946]/10 hover:bg-[#E63946]/20 transition-all md:col-span-2"
          data-testid="share-kit-btn"
        >
          <div className="w-12 h-12 rounded-xl bg-[#E63946] flex items-center justify-center flex-shrink-0">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div className="text-left flex-1">
            <p className="font-bold text-[#1D3557]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Download the Reading Group Kit <span className="ml-2 text-xs px-2 py-0.5 bg-[#E63946] text-white rounded-full uppercase tracking-wider">Free</span>
            </p>
            <p className="text-sm text-[#1D3557]/70 font-medium">15 discussion questions for teachers, book clubs &amp; parents — print or save as PDF</p>
          </div>
        </Link>
      </div>

      {variant === "full" && (
        <>
          {/* Already read it? Leave a review */}
          <a
            href={AMAZON_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-4 p-4 rounded-2xl border-3 border-[#E9C46A] bg-gradient-to-r from-[#E9C46A]/30 to-[#E9C46A]/10 hover:from-[#E9C46A]/50 hover:to-[#E9C46A]/20 transition-all mb-6"
            data-testid="share-review-btn"
          >
            <div className="w-12 h-12 rounded-xl bg-[#E9C46A] flex items-center justify-center flex-shrink-0">
              <Star className="w-6 h-6 text-[#1D3557]" fill="#1D3557" />
            </div>
            <div className="text-left flex-1">
              <p className="font-bold text-[#1D3557]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Already read it? Leave a review on Amazon
                <span className="ml-2 text-xs px-2 py-0.5 bg-[#1D3557] text-white rounded-full uppercase tracking-wider">Big help!</span>
              </p>
              <p className="text-sm text-[#1D3557]/70 font-medium">
                Reviews boost the book's search ranking — the first few make a huge difference for an indie author.
              </p>
            </div>
            <span className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1D3557] text-white font-bold text-sm flex-shrink-0">
              <Star className="w-4 h-4" fill="currentColor" /> Review
            </span>
          </a>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-[#1D3557]/15" />
            <span className="text-xs text-[#1D3557]/60 font-bold uppercase tracking-wider">Or share on social</span>
            <div className="flex-1 h-px bg-[#1D3557]/15" />
          </div>

          <div className="flex flex-wrap gap-3 justify-center">
            <a
              href={SHARE_URLS.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-3 rounded-xl bg-[#1D3557] text-white font-bold hover:bg-[#2A9D8F] transition-colors inline-flex items-center gap-2"
              data-testid="share-twitter-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              X / Twitter
            </a>
            <a
              href={SHARE_URLS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-3 rounded-xl bg-[#457B9D] text-white font-bold hover:bg-[#1D3557] transition-colors inline-flex items-center gap-2"
              data-testid="share-facebook-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.017 1.792-4.683 4.533-4.683 1.312 0 2.686.235 2.686.235v2.965h-1.514c-1.491 0-1.956.93-1.956 1.886v2.265h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073"/></svg>
              Facebook
            </a>
            <a
              href={SHARE_URLS.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-3 rounded-xl bg-[#2A9D8F] text-white font-bold hover:bg-[#1D3557] transition-colors inline-flex items-center gap-2"
              data-testid="share-whatsapp-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413"/></svg>
              WhatsApp
            </a>
            <button
              onClick={copyLink}
              className="px-5 py-3 rounded-xl bg-[#E63946] text-white font-bold hover:bg-[#1D3557] transition-colors inline-flex items-center gap-2"
              data-testid="share-copylink-btn"
            >
              {copied ? <><Check className="w-5 h-5" /> Copied!</> : <><ShoppingBag className="w-5 h-5" /> Copy Amazon Link</>}
            </button>
          </div>
        </>
      )}
    </motion.div>
  );
};

export default ShareSection;
